"use strict";

/**
 * <strong>Purpose:</strong> <br>
 * Provides utilities for working with DOM elements including functions to help with
 * the ability to determine the position and visibility of DOM elements based on obscuring components,
 * and finding and focusing DOM elements relative to other DOM elements.
 * <br><br>
 *
 * @module mw-utils/domUtils
 *
 * @copyright Copyright 2015 The MathWorks, Inc.
 */

define(["dojo/sniff"], function (has) {

    var domUtils = {};

    domUtils.messages = {
        INVALID_ELEMENT: "The input parameter must be an instance of an Element object.",
        INVALID_STRING: "The input parameter must be of type 'string'.",
        INVALID_FOCUS_EVENT: "The input parameter must be a 'FocusEvent' object.",
        INVALID_STYLE: "The style specified is invalid.",
        INVALID_VALUE: "The value specified is invalid.",
        INTEGER_INDEX: "index argument expects an integer.",
        STRING_INDEX: "index argument expects an string.",
        SPOUSE_NODE: "A function which returns a spouse node if it exists must be provided."
    };

    domUtils._validateElement = function ( /*Element*/element) {
        if (!(element instanceof Element)) {
            throw new Error(domUtils.messages.INVALID_ELEMENT);
        }
    };

    domUtils._validateString = function ( /*String*/value) {
        if (!(typeof value !== "String")) {
            throw new Error(domUtils.messages.INVALID_STRING);
        }
    };

    // Events
    domUtils.isLeftMouseButton = function ( /*Event*/event) {
        if (event.synthetic === true && has("ie")) {
            return event.button === 1;
        } else {
            return event.button === 0;
        }
    };

    domUtils.parseFocusEvent = function ( /*Event*/event) {
        if (!(event instanceof FocusEvent)) {
            throw new Error(domUtils.messages.INVALID_FOCUS_EVENT);
        }

        var evtData = {};

        evtData.lostFocusNode = event.target;
        evtData.gainedFocusNode = has("chrome") || has("safari") ? event.relatedTarget : document.activeElement;
        evtData.type = event.type;
        evtData.eventdata = event;

        return evtData;
    };

    // General
    domUtils.getParent = function ( /*HTMLElement*/element) {
        domUtils._validateElement(element);

        return element.parentNode;
    };

    domUtils.findParentNode = function (node, /*String*/selector, /*String?*/modifier) {
        var parent = null,
            parentPopupId;

        if (node.domNode) {
            node = node.domNode;
        }

        while (node && node !== window.document) {
            if (domUtils.hasAttr(node, selector)) {
                parent = node;
                break;
            }

            if (domUtils.hasAttrValue(node, selector, modifier)) {
                parent = node;
                break;
            }

            if (domUtils.containsClass(node, selector)) {
                parent = node;
                break;
            }

            parentPopupId = domUtils.getAttr(node, "dijitpopupparent");

            // If the widget is in one of the popups, then go up the popup parent chain
            if (parentPopupId) {
                node = domUtils.getById(parentPopupId);
                // If its not one of the popups, then go to parent
            } else {
                node = node.parentNode;
            }
        }

        return parent;
    };

    domUtils.getNodeType = function ( /*HTMLElement*/element) {
        var nodeTypeMap = {
            1: "Element",
            3: "Text",
            7: "Processing Instruction",
            8: "Comment",
            9: "Document",
            10: "Document Type",
            11: "Document Fragment"
        };

        domUtils._validateElement(element);

        return nodeTypeMap[element.nodeType];
    };

    // DOM Queries
    domUtils.getById = function ( /*String*/id) {
        domUtils._validateString(id);
        return window.document.getElementById(id);
    };
    domUtils.getByClass = function ( /*String*/className) {
        domUtils._validateString(className);
        return window.document.getElementsByClassName(className);
    };
    domUtils.getByAttr = function ( /*String*/attr) {
        domUtils._validateString(attr);
        return window.document.querySelectorAll("[" + attr + "]"); //data-foo=value
    };
    domUtils.getByAttrValue = function ( /*String*/attr, /*String*/value) {
        domUtils._validateString(attr);
        domUtils._validateString(value);
        return window.document.querySelectorAll("[" + attr + "=" + value.replace(/['".\\]/g, '\\$&').replace(/[\\]/g, '\\$&') + "]");
    };
    domUtils.getByName = function ( /*String*/name) {
        domUtils._validateString(name);
        return window.document.getElementById("[name=" + name + "]");
    };
    domUtils.getByTag = function ( /*String*/tag) {
        domUtils._validateString(tag);
        return window.document.getElementsByTagName(tag);
    };

    // Styles
    domUtils.getComputedStyle = function ( /*HTMLElement*/element, /*String*/style) {
        domUtils._validateElement(element);
        if (!style) {
            // Second argument to window.getComputedStyle is for pseudo-elements,
            // and is not needed in this utility
            return window.getComputedStyle(element, null);
        } else {
            domUtils._validateString(style);
            return window.getComputedStyle(element, null)[style];
        }
    };

    domUtils.getStyle = function ( /*HTMLElement*/element, /*String*/style) {
        domUtils._validateElement(element);
        domUtils._validateString(style);
        if (element.style[style] === undefined) {
            throw new Error(domUtils.messages.INVALID_STYLE);
        }
        return element.style[style];
    };

    domUtils.setStyle = function ( /*HTMLElement*/element, /*String*/style, /*String*/value) {
        domUtils._validateElement(element);
        domUtils._validateString(style);
        domUtils._validateString(value);
        element.style[style] = value;
        if (element.style[style] !== value) {
            throw new Error(domUtils.messages.INVALID_VALUE);
        }
    };

    // Attributes
    domUtils.hasAttr = function ( /*HTMLElement*/element, /*String*/name) {
        domUtils._validateElement(element);
        domUtils._validateString(name);

        return element.hasAttribute(name);
    };
    domUtils.hasAttrValue = function ( /*HTMLElement*/element, /*String*/name, /*String*/value) {
        domUtils._validateElement(element);
        domUtils._validateString(name);
        domUtils._validateString(value);

        return element.hasAttribute(name) && element.getAttribute(name) === value;
    };
    domUtils.getAttr = function ( /*HTMLElement*/element, /*String*/name) {
        domUtils._validateElement(element);
        domUtils._validateString(name);

        return element.getAttribute(name);
    };
    domUtils.setAttr = function ( /*HTMLElement*/element, /*String*/name, /*String*/value) {
        domUtils._validateElement(element);
        domUtils._validateString(name);
        domUtils._validateString(value);

        element.setAttribute(name, value);
    };
    domUtils.addAttr = function ( /*HTMLElement*/element, /*String*/name) {
        domUtils._validateElement(element);
        domUtils._validateString(name);

        element.setAttribute(name, '');
    };
    domUtils.removeAttr = function ( /*HTMLElement*/element, /*String*/name) {
        domUtils._validateElement(element);
        domUtils._validateString(name);

        element.removeAttribute(name);
    };

    // Classes
    domUtils.getClasses = function ( /*HTMLElement*/element) {
        domUtils._validateElement(element);

        if (element.classList) {
            return element.classList;
        } else {
            // Mimic a DOMTokenList
            return {
                _element: element,
                _getClassArray: function _getClassArray() {
                    return this._element.className.split(" ");
                },
                _updateClassName: function _updateClassName(arr) {
                    this._element.className = arr.join(" ");
                    this.length = arr.length;
                },
                length: function () {
                    return element.className.split(" ").length;
                }(),
                item: function item(idx) {
                    return this._getClassArray()[idx];
                },
                contains: function contains(token) {
                    var arr = this._getClassArray();
                    for (var i = 0; i < arr.length; i++) {
                        if (arr[i] === token) {
                            return true;
                        }
                    }
                    return false;
                },
                add: function add(val) {
                    var arr = this._getClassArray();
                    arr.push(val);
                    this._updateClassName(arr);
                },
                remove: function remove(val) {
                    var arr = this._getClassArray();
                    var idx = arr.indexOf(val);
                    if (idx >= 0) {
                        arr.splice(idx, 1);
                        this._updateClassName(arr);
                    }
                },
                toggle: function toggle(val) {
                    if (this.contains(val)) {
                        this.remove(val);
                        return false;
                    } else {
                        this.add(val);
                        return true;
                    }
                }
            };
        }
    };

    domUtils.getClass = function ( /*HTMLElement*/element, /*Number*/index) {
        if (index == null || typeof index !== "number" || index < 0 || index % 1 !== 0) {
            throw new Error(domUtils.messages.INTEGER_INDEX);
        }

        return domUtils.getClasses(element).item(index);
    };

    domUtils.addClass = function ( /*HTMLElement*/element, /*String*/className) {
        if (className == null || typeof className !== "string") {
            throw new Error(domUtils.messages.STRING_INDEX);
        }

        return domUtils.getClasses(element).add(className);
    };

    domUtils.removeClass = function ( /*HTMLElement*/element, /*String*/className) {
        if (className == null || typeof className !== "string") {
            throw new Error(domUtils.messages.STRING_INDEX);
        }

        return domUtils.getClasses(element).remove(className);
    };

    domUtils.toggleClass = function ( /*HTMLElement*/element, /*String*/className) {
        if (className == null || typeof className !== "string") {
            throw new Error(domUtils.messages.STRING_INDEX);
        }

        return domUtils.getClasses(element).toggle(className);
    };

    domUtils.containsClass = function ( /*HTMLElement*/element, /*String*/className) {
        if (className == null || typeof className !== "string") {
            throw new Error(domUtils.messages.STRING_INDEX);
        }

        return domUtils.getClasses(element).contains(className);
    };

    domUtils.classCount = function ( /*HTMLElement*/element) {
        return domUtils.getClasses(element).length;
    };

    // Containers
    domUtils.getChildCount = function ( /*HTMLElement*/element) {
        domUtils._validateElement(element);

        return element.childElementCount;
    };

    domUtils.getChildren = function ( /*HTMLElement*/element) {
        domUtils._validateElement(element);

        // Use element.children instead of elements.childNodes because we only care about elements of nodeType === 1
        return element.children;
    };

    domUtils.isDescendant = function ( /*HTMLElement*/child, /*HTMLElement*/ancestor, /*Boolean?*/inclusive) {
        domUtils._validateElement(child);
        domUtils._validateElement(ancestor);

        if (inclusive) {
            if (child === ancestor) {
                return true;
            }
        }
        while (child && child !== window.document) {
            child = domUtils.getParent(child);
            if (child === ancestor) {
                return true;
            }
        }
        return false;
    };

    // In the DOM hierarchy, an in-law is a node who is not a direct descendant or ancestor, but has
    // a similar relationship through a spouse node.  A spouse node is a node who is "married" to
    // another node semantically, but not a descendant or ancestor in the dom hierarchy.  For
    // example a spouse node to a drop down button would be the popup node, and the popup's
    // descendant elements would be in-laws of the drop down and all of it's descendants and
    // ancestors.
    // Note: This utility includes spouses in the descendant hierarchy.
    domUtils.isDescendantOrInLaw = function ( /*HTMLElement*/child, /*HTMLElement*/ancestor,
    /*Function*/getSpouseOfNode, /*Boolean?*/inclusive) {
        var parentOrSpouse, isDescendant;

        if (typeof getSpouseOfNode !== "function") {
            throw new Error(domUtils.messages.SPOUSE_NODE);
        }

        // node validation is performed by domUtils.isDescendant, so no need to duplicate that effort here

        isDescendant = domUtils.isDescendant(child, ancestor, inclusive);

        if (isDescendant === false) {
            var getParentOrSpouse = function getParentOrSpouse(node) {
                return getSpouseOfNode(node) || node.parentNode;
            };

            // If node is a child of a sub-menu then return true
            parentOrSpouse = getParentOrSpouse(child);

            if (parentOrSpouse && parentOrSpouse !== window.document) {
                isDescendant = domUtils.isDescendantOrInLaw(parentOrSpouse, ancestor, getSpouseOfNode, inclusive);
            }
        }

        return isDescendant;
    };

    domUtils.getFirstChild = function ( /*HTMLElement*/element) {
        domUtils._validateElement(element);

        if (!domUtils.isContainer(element) || domUtils.getChildCount(element) < 1) {
            return null;
        }

        return domUtils.getChildren(element)[0];
    };

    domUtils.getLastChild = function ( /*HTMLElement*/element) {
        var childCount;

        domUtils._validateElement(element);

        childCount = domUtils.getChildCount(element);

        if (!domUtils.isContainer(element) || childCount < 1) {
            return null;
        }

        return domUtils.getChildren(element)[childCount - 1];
    };

    domUtils.isContainer = function ( /*HTMLElement*/element) {
        domUtils._validateElement(element);

        return domUtils.getChildCount(element) > 0;
    };

    // Element Navigation
    domUtils.getAdjacentElement = function ( /*HTMLElement*/element, /*HTMLElement?*/direction) {
        domUtils._validateElement(element);

        if (!direction || ["next", "previous"].indexOf(direction) < 0) {
            direction = "next";
        }

        element = element[direction + "Sibling"];

        return element;
    };

    // Element Position
    domUtils.getElementPositionInViewPort = function ( /*HTMLElement*/element) {
        var rect;

        domUtils._validateElement(element);

        rect = element.getBoundingClientRect();

        // IE8 and below does not include rect.width and rect.height
        return { x: rect.left, y: rect.top, w: rect.right - rect.left, h: rect.bottom - rect.top };
    };

    domUtils.getElementPositionOnPage = function ( /*HTMLElement*/element) {
        var pos;

        domUtils._validateElement(element);

        pos = domUtils.getElementPositionInViewPort(element);

        pos.x += window.pageXOffset;
        pos.y += window.pageYOffset;

        return pos;
    };

    // Element Focusability/Visibility
    domUtils.isFocusable = function ( /*HTMLElement*/element) {
        domUtils._validateElement(element);

        return element && !element.hasAttribute("disabled") && (!element.hasAttribute("aria-disabled") || element.getAttribute("aria-disabled") !== "true") && element.style.display !== "none" && !element.hasAttribute("data-refuse-key-nav"); // Used in menu and form widgets
    };

    domUtils.isElementInViewport = function ( /*HTMLElement*/element) {
        var rect, vWidth, vHeight, efp;

        domUtils._validateElement(element);

        rect = element.getBoundingClientRect();
        vWidth = window.innerWidth || doc.documentElement.clientWidth;
        vHeight = window.innerHeight || doc.documentElement.clientHeight;
        efp = function efp(x, y) {
            return document.elementFromPoint(x, y);
        };

        // Return false if it's not in the viewport
        if (rect.right < 0 || rect.bottom < 0 || rect.left > vWidth || rect.top > vHeight) {
            return false;
        }

        // Return true if any of its four corners are visible
        return element.contains(efp(rect.left, rect.top)) || element.contains(efp(rect.right, rect.top)) || element.contains(efp(rect.right, rect.bottom)) || element.contains(efp(rect.left, rect.bottom));
    };

    domUtils._obscuredProps = {};
    domUtils._obscuredProps.sawPosAbsolute = false;
    domUtils._obscuredProps.elementPosition = null;

    domUtils.isElementObscuredByParent = function ( /*HTMLElement*/element, /*Boolean {internal}?*/_calledByIsElementObscuredByAncestor) {
        // Basic traversal logic is borrowed from dijit\place.js "around" method
        var elementPosition, sawPosAbsolute, parent, obscured;

        domUtils._validateElement(element);

        obscured = false;

        // Compute position and size of visible part of element and see if it is partially hidden by ancestor elements
        if (parent = domUtils.getParent(element)) {
            if (!_calledByIsElementObscuredByAncestor) {
                domUtils._obscuredProps.elementPosition = domUtils.getElementPositionOnPage(element);
                domUtils._obscuredProps.sawPosAbsolute = window.getComputedStyle(element, null).position === "absolute"; // ignore elements between position:relative and position:absolute
            }

            elementPosition = domUtils._obscuredProps.elementPosition;
            sawPosAbsolute = domUtils._obscuredProps.sawPosAbsolute;

            if (parent && domUtils.getNodeType(parent) === "Element" && parent.nodeName !== "BODY") {
                //ignoring the body will help performance
                var parentPos = domUtils.getElementPositionOnPage(parent),
                    pcs = window.getComputedStyle(parent, null);
                if (/relative|absolute/.test(pcs.position)) {
                    sawPosAbsolute = domUtils._sawPosAbsolute = false;
                }
                if (!sawPosAbsolute && /hidden|auto|scroll/.test(pcs.overflow)) {
                    obscured = elementPosition.y < parentPos.y || elementPosition.y + elementPosition.h > parentPos.y + parentPos.h || elementPosition.x < parentPos.x || elementPosition.x + elementPosition.w > parentPos.x + parentPos.w;
                }
            }
        }

        return obscured;
    };

    domUtils.isElementObscuredByAncestor = function ( /*HTMLElement*/element) {
        var obscured, parent;

        domUtils._validateElement(element);

        obscured = false;

        // If the element has a parent then check if it is obscured, otherwise return false
        domUtils._obscuredProps.elementPosition = domUtils.getElementPositionOnPage(element);
        domUtils._obscuredProps.sawPosAbsolute = window.getComputedStyle(element, null).position === "absolute";
        while ((parent = domUtils.getParent(element)) && domUtils.getNodeType(parent) === "Element" && parent.nodeName !== "BODY") {
            //ignoring the body will help performance
            obscured = domUtils.isElementObscuredByParent(element, true);
            element = parent;
        }

        return obscured;
    };

    return domUtils;
});
//# sourceMappingURL=domUtils.js.map
